import React, { useReducer } from 'react';
import { styled } from '@compiled/react';
import Form, { FormHeader, RequiredAsterisk } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
// eslint-disable-next-line import/order
import { token } from '@atlaskit/tokens';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { injectIntl, type IntlShape } from '@atlassian/jira-intl';
import {
	AnalyticsEventToProps,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import DirtyFormWarning from '@atlassian/jira-servicedesk-common/src/navigation/blocker';
import { CUSTOMER_ACCESS } from '@atlassian/jira-servicedesk-customer-permissions/src/common/types.tsx';
import { CustomerAccessStatus } from '@atlassian/jira-servicedesk-customer-permissions/src/common/ui/customer-access-status/index.tsx';
import {
	updateProjectName,
	updateProjectDescription,
	updatePortalAnnouncements,
} from '../../services';
import IconUpload from './icon-upload';
import messages from './messages';
import Announcements from './portal-announcements';
import { PORTAL_RADIO_OPTIONS } from './portal-announcements/types';
import ServiceDeskForm from './service-desk-information';
import type {
	ServiceDeskReducerAction,
	ServiceDeskReducerState,
	RadioReducerAction,
	RadioReducerState,
} from './types';

type Props = {
	canAdministerJira: boolean;
	canAgentsManageHelpCenterAnnouncement: boolean;
	canAgentsManagePortalAnnouncement: boolean;
	customerPermissionsLink: string;
	baseUrl: string;
	isAdmin: boolean;
	isNextGen: boolean;
	isOpenAccess: boolean;
	portalLogoUrl: string;
	portalShortLink: string;
	projectId: number;
	projectKey: string;
	startDesc: string;
	startName: string;
	intl: IntlShape;
};

const FormControllerInformation = AnalyticsEventToProps('ServicedeskInformationForm', {
	onSubmit: 'submit',
})(Form);

const FormControllerAnnouncements = AnalyticsEventToProps('AnnouncementsForm', {
	onSubmit: 'submit',
})(Form);

const serviceDeskReducer = (state: ServiceDeskReducerState, action: ServiceDeskReducerAction) => {
	switch (action.type) {
		case 'loading':
			return { ...state, isLoading: true };
		case 'complete':
			return {
				currName: action.payload.name,
				currDesc: action.payload.description,
				formFailedToSubmit: false,
				isLoading: false,
			};
		default:
			return { ...state, formFailedToSubmit: true, isLoading: false };
	}
};

const radioReducer = (state: RadioReducerState, action: RadioReducerAction) => {
	switch (action.type) {
		case 'loading':
			return { ...state, isLoading: true };
		case 'complete':
			return {
				defaultOption: action.payload.newData,
				formFailedToSubmit: false,
				isLoading: false,
			};
		default:
			return { ...state, formFailedToSubmit: true, isLoading: false };
	}
};

const CustomerPortalForms = (props: Props) => {
	const {
		intl: { formatMessage },
		baseUrl,
		projectKey,
		projectId,
		isAdmin,
		isNextGen,
		isOpenAccess,
		startName,
		portalLogoUrl,
		canAgentsManagePortalAnnouncement,
		canAgentsManageHelpCenterAnnouncement,
		canAdministerJira,
		startDesc,
		portalShortLink,
		customerPermissionsLink,
	} = props;

	const serviceDeskInit = {
		currName: startName,
		currDesc: startDesc,
		isLoading: false,
		formFailedToSubmit: false,
	};

	const initialOption = canAgentsManagePortalAnnouncement
		? PORTAL_RADIO_OPTIONS.AGENTS_AND_ADMINS
		: PORTAL_RADIO_OPTIONS.ONLY_ADMINS;

	const radioInit = {
		defaultOption: initialOption,
		isLoading: false,
		formFailedToSubmit: false,
	};

	const [serviceDeskState, serviceDeskDispatch] = useReducer(serviceDeskReducer, serviceDeskInit);
	const [radioState, radioDispatch] = useReducer(radioReducer, radioInit);

	const handleServiceDeskSubmit = async (
		data: {
			name: string;
			description: string;
		},
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		analyticsEvent: any,
	) => {
		serviceDeskDispatch({ type: 'loading', payload: { name: '', description: '' } });
		const { name, description } = data;
		try {
			if (serviceDeskState.currName !== name) {
				await updateProjectName({ baseUrl, projectKey, name });
			}
			if (serviceDeskState.currDesc !== description) {
				await updateProjectDescription({ baseUrl, projectKey, description });
			}
			fireTrackAnalytics(analyticsEvent, 'portalNameAndDescription submitted', {
				nameUpdated: serviceDeskState.currName !== name,
				descUpdated: serviceDeskState.currDesc !== description,
			});
			serviceDeskDispatch({ type: 'complete', payload: { name, description } });
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			serviceDeskDispatch({ type: 'failure', payload: { name: '', description: '' } });
			fireTrackAnalytics(analyticsEvent, 'portalNameAndDescription failed', {
				error: error.toString(),
			});
		}
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleRadioSubmit = async (data: any, analyticsEvent: any) => {
		radioDispatch({ type: 'loading', payload: { newData: '' } });
		const canAgentsAdd = data.radioField === PORTAL_RADIO_OPTIONS.AGENTS_AND_ADMINS;
		try {
			await updatePortalAnnouncements({ baseUrl, projectKey, canAgentsAdd });
			fireTrackAnalytics(analyticsEvent, 'customerPortalRadio submitted', {
				canAgentsAdd,
			});
			radioDispatch({ type: 'complete', payload: { newData: data.radioField } });
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			radioDispatch({ type: 'failure', payload: { newData: '' } });
			fireTrackAnalytics(analyticsEvent, 'customerPortalRadio failed', {
				error: error.toString(),
			});
		}
	};

	return (
		<Container>
			<Heading as="h2" size="small">
				{formatMessage(messages.serviceProjectInformation)}
			</Heading>
			<FormHeader>
				{formatMessage(messages.instructionForMandatoryField)}
				<RequiredAsterisk />
			</FormHeader>
			<FormControllerInformation
				// @ts-expect-error - TS7006 - Parameter 'data' implicitly has an 'any' type. | TS7006 - Parameter '_x' implicitly has an 'any' type. | TS7006 - Parameter '_y' implicitly has an 'any' type. | TS7006 - Parameter 'analyticsEvent' implicitly has an 'any' type.
				onSubmit={(data, _x, _y, analyticsEvent) => {
					handleServiceDeskSubmit(data, analyticsEvent);
				}}
			>
				{({
					// @ts-expect-error - TS7031 - Binding element 'serviceDeskFormProps' implicitly has an 'any' type.
					formProps: serviceDeskFormProps,
					// @ts-expect-error - TS7031 - Binding element 'serviceDeskIsDirty' implicitly has an 'any' type.
					dirty: serviceDeskIsDirty,
					// @ts-expect-error - TS7031 - Binding element 'serviceDeskSubmitting' implicitly has an 'any' type.
					submitting: serviceDeskSubmitting,
				}) => (
					<>
						{isAdmin ? (
							<FormContainer>
								<ServiceDeskForm
									submitting={serviceDeskSubmitting}
									formProps={serviceDeskFormProps}
									state={serviceDeskState}
									dirty={serviceDeskIsDirty}
								/>
							</FormContainer>
						) : (
							<>
								<Heading level="h200">{formatMessage(messages.name)}</Heading>
								<p>{startName}</p>
								<Heading level="h200">{formatMessage(messages.introText)}</Heading>
								<p>{startDesc}</p>
							</>
						)}
						<IconUpload
							baseUrl={baseUrl}
							projectKey={projectKey}
							projectId={projectId}
							portalLogoUrl={portalLogoUrl}
						/>
						{!isNextGen && (
							<>
								<HeadingContainer>
									<Heading as="h2" size="small">
										{formatMessage(messages.channelAccess)}
									</Heading>
								</HeadingContainer>

								<CustomerAccessStatus
									accessType={isOpenAccess ? CUSTOMER_ACCESS.PUBLIC : CUSTOMER_ACCESS.RESTRICTED}
									settingsLink={customerPermissionsLink}
								/>
							</>
						)}
						<HeadingContainer>
							<Heading as="h2" size="small">
								{formatMessage(messages.announcements)}
							</Heading>
						</HeadingContainer>
						<FormControllerAnnouncements
							// @ts-expect-error - TS7006 - Parameter 'data' implicitly has an 'any' type. | TS7006 - Parameter '_x' implicitly has an 'any' type. | TS7006 - Parameter '_y' implicitly has an 'any' type. | TS7006 - Parameter 'analyticsEvent' implicitly has an 'any' type.
							onSubmit={(data, _x, _y, analyticsEvent) => {
								handleRadioSubmit(data, analyticsEvent);
							}}
						>
							{({
								// @ts-expect-error - TS7031 - Binding element 'radioFormProps' implicitly has an 'any' type.
								formProps: radioFormProps,
								// @ts-expect-error - TS7031 - Binding element 'radioIsDirty' implicitly has an 'any' type.
								dirty: radioIsDirty,
								// @ts-expect-error - TS7031 - Binding element 'radioSubmitting' implicitly has an 'any' type.
								submitting: radioSubmitting,
							}) => (
								<>
									<Announcements
										formProps={radioFormProps}
										submitting={radioSubmitting}
										state={radioState}
										canAgentsManageHelpCenterAnnouncement={canAgentsManageHelpCenterAnnouncement}
										canAdministerJira={!!canAdministerJira}
										portalShortLink={portalShortLink}
										projectId={projectId}
										dirty={radioIsDirty}
									/>
									<DirtyFormWarning
										isDirty={serviceDeskIsDirty || radioIsDirty}
										withSaveButton={false}
										prompt={messages.noSavePrompt}
									/>
								</>
							)}
						</FormControllerAnnouncements>
					</>
				)}
			</FormControllerInformation>
		</Container>
	);
};

export default injectIntl(CustomerPortalForms);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${gridSize * 100}px`,
	paddingBottom: token('space.400', '32px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FormContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${gridSize * 45}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingContainer = styled.div({
	marginTop: token('space.300', '24px'),
});
